/**
 * The workspace store: one reactive owner of the spine, shared by every page.
 *
 * Deliberately a module singleton rather than a provide/inject: there is exactly one workspace
 * per tab, and passing it through four levels of route components buys nothing.
 */
import { computed, ref } from 'vue';

import { CHAPTER } from '../data/ma4-chapter1';

import {
  SEED_COURSES, newChapter, newLogEntry, newTask, progressOf, taskIndex,
  type Chapter, type Course, type LogEntry, type LogKind, type Task, type TaskStatus,
} from './curriculum';
import {
  browse, copyName, detachSheet, insertSheet, moveSheet, newProject, newProjectSheet,
  nextSheetName, renameSheet, sheetAt, touch,
  type Project, type ProjectAccent, type ProjectSheet,
} from './projects';
import { emptyScene } from './scene';
import {
  SCRATCH_ID, STORES, allRecords, deleteRecord, deleteSheet, deleteWhere, getRecord, loadSheet,
  newSheet, putMany, putRecord, saveSheet,
} from './storage';

const courses = ref<Course[]>([]);
const chapters = ref<Chapter[]>([]);
const tasks = ref<Task[]>([]);
const log = ref<LogEntry[]>([]);
const projects = ref<Project[]>([]);
const ready = ref(false);

/** Chapters seeded from the two university courses whose syllabus content is settled. */
function seedChapters(courseId: string, code: string): Chapter[] {
  // The other courses get their titles when their books arrive, and not before. Seeding them
  // with invented ranges of (i+1)*100+1 put LinAlg chapter 10 and Algebra chapter 10 both at
  // 1001–1060, so the number 1042 was claimed by three different chapters at once and which one
  // answered depended on array order. A wrong seed is worse than an empty one.
  // Matte 4's first chapter is transcribed from the owner's own course book. Its numbering is
  // flat — 1001 to 1143 — not the `chapter·section·index` scheme the published books use, which
  // is exactly why the chapter model stores a range rather than a formula.
  if (code === 'Ma4') {
    return [newChapter(courseId, CHAPTER.number, CHAPTER.title, CHAPTER.from, CHAPTER.to, 1)];
  }
  // Matte 5 and the university courses wait for their own books. A wrong seed is worse than an
  // empty one, because it has to be found before it can be fixed.
  return [];
}


// ── The phantom-chapter repair ─────────────────────────────────────────────────────────────

/**
 * Courses whose chapters this app once invented, and no longer does.
 *
 * An older seed gave every course ten placeholder chapters, chapter n covering n·100+1 to
 * n·100+60. LinAlg chapter 10 and Algebra chapter 10 therefore both claimed 1001–1060, which is
 * inside Ma4's real chapter 1 (1001–1143) — so 1042 belonged to three chapters at once and the
 * hub offered "1122 · Egenvärden" under a heading that said Matematik 4. Deleting the seed fixed
 * new installs and did nothing for the only database that had the problem.
 */
const PHANTOM_SEED_CODES = new Set(['LinAlg', 'Algebra']);

interface Repairs {
  id: string;
  phantomChaptersAt?: number;
  phantomChaptersRemoved?: number;
}

const REPAIRS_ID = 'repairs';

/** The old seed's exact shape. A chapter the owner typed himself will not match it. */
const isPlaceholderRange = (chapter: Chapter): boolean => {
  const n = Number(chapter.number);
  if (!Number.isInteger(n) || n < 1) return false;
  const span = chapter.to - chapter.from;
  return chapter.from === n * 100 + 1 && (span === 59 || span === 60);
};

/**
 * Delete the invented chapters, once, and only where nothing was ever worked in them.
 *
 * Two rules make this safe to run against a real database. A chapter with any task or any log
 * entry against it is kept whatever it looks like — work outranks tidiness, and a phantom that
 * was used is not a phantom any more. And the run is recorded in `meta`, so it cannot fire again
 * later and take away LinAlg chapters the owner has since entered for himself.
 */
async function repairPhantomChapters(
  allCourses: readonly Course[],
  allChapters: readonly Chapter[],
  allTasks: readonly Task[],
  allLog: readonly LogEntry[],
): Promise<Chapter[]> {
  const seen = await getRecord<Repairs>(STORES.meta, REPAIRS_ID);
  if (seen?.phantomChaptersAt) return [...allChapters];

  const seeded = new Set(
    allCourses.filter(course => PHANTOM_SEED_CODES.has(course.code)).map(course => course.id));
  const touched = new Set([
    ...allTasks.map(task => task.chapterId),
    ...allLog.map(entry => entry.chapterId),
  ].filter(Boolean));

  const doomed = allChapters.filter(chapter =>
    seeded.has(chapter.courseId) && !touched.has(chapter.id) && isPlaceholderRange(chapter));

  for (const chapter of doomed) await deleteRecord(STORES.chapters, chapter.id);
  await putRecord<Repairs>(STORES.meta, {
    id: REPAIRS_ID,
    phantomChaptersAt: Date.now(),
    phantomChaptersRemoved: doomed.length,
  });

  const gone = new Set(doomed.map(chapter => chapter.id));
  return allChapters.filter(chapter => !gone.has(chapter.id));
}

export async function load(): Promise<void> {
  if (ready.value) return;
  const [storedCourses, storedChapters, storedTasks, storedLog, storedProjects] = await Promise.all([
    allRecords<Course>(STORES.courses),
    allRecords<Chapter>(STORES.chapters),
    allRecords<Task>(STORES.tasks),
    allRecords<LogEntry>(STORES.log),
    allRecords<Project>(STORES.projects),
  ]);

  if (storedCourses.length === 0) {
    const seeded = SEED_COURSES;
    const seededChapters = seeded.flatMap(course => seedChapters(course.id, course.code));
    await putMany(STORES.courses, seeded);
    await putMany(STORES.chapters, seededChapters);
    courses.value = seeded;
    chapters.value = seededChapters;
  } else {
    courses.value = storedCourses.sort((a, b) => a.order - b.order);
    chapters.value = await repairPhantomChapters(
      storedCourses, storedChapters, storedTasks, storedLog);
  }
  tasks.value = storedTasks;
  log.value = storedLog.sort((a, b) => b.at - a.at);
  // Projects are never seeded. An invented project is someone else's work in your workspace.
  projects.value = storedProjects;
  ready.value = true;
}

// ── Reads ──────────────────────────────────────────────────────────────────────────────────

export const useWorkspace = () => ({
  ready,
  courses: computed(() => [...courses.value].sort((a, b) => a.order - b.order)),
  chapters,
  tasks,
  log,
  projects,
});

export const courseById = (id: string): Course | undefined =>
  courses.value.find(course => course.id === id);

export const chapterById = (id: string): Chapter | undefined =>
  chapters.value.find(chapter => chapter.id === id);

export const chaptersOf = (courseId: string): Chapter[] =>
  chapters.value.filter(chapter => chapter.courseId === courseId).sort((a, b) => a.order - b.order);

export const tasksOf = (chapterId: string): Task[] =>
  tasks.value.filter(task => task.chapterId === chapterId).sort((a, b) => a.index - b.index);

export const taskByNumber = (chapterId: string, number: string): Task | undefined =>
  tasks.value.find(task => task.chapterId === chapterId && task.number === number);

export const chapterProgress = (chapter: Chapter) => progressOf(chapter, tasks.value);

/** Every task the owner got wrong or is stuck on, newest first. The error log. */
export const troubleTasks = computed(() =>
  tasks.value
    .filter(task => task.status === 'wrong' || task.status === 'stuck')
    .sort((a, b) => b.updatedAt - a.updatedAt));

// ── Writes ─────────────────────────────────────────────────────────────────────────────────

/** The task most recently worked on — what "continue" means. */
export const lastTouched = computed(() => {
  const worked = tasks.value.filter(t => t.status !== 'untouched');
  return worked.sort((a, b) => b.updatedAt - a.updatedAt)[0] ?? null;
});

export async function record(
  kind: LogKind,
  fields: { courseId?: string; chapterId?: string; taskId?: string; label: string; detail?: string; minutes?: number },
): Promise<void> {
  const entry = newLogEntry({
    kind,
    courseId: fields.courseId ?? '',
    chapterId: fields.chapterId ?? '',
    taskId: fields.taskId ?? '',
    label: fields.label,
    detail: fields.detail ?? '',
    minutes: fields.minutes ?? 0,
  });
  log.value = [entry, ...log.value];
  await putRecord(STORES.log, entry);
}

export async function saveCourse(course: Course): Promise<void> {
  const index = courses.value.findIndex(c => c.id === course.id);
  courses.value = index >= 0
    ? courses.value.map(c => (c.id === course.id ? course : c))
    : [...courses.value, course];
  await putRecord(STORES.courses, course);
}

export async function saveChapter(chapter: Chapter): Promise<void> {
  const index = chapters.value.findIndex(c => c.id === chapter.id);
  chapters.value = index >= 0
    ? chapters.value.map(c => (c.id === chapter.id ? chapter : c))
    : [...chapters.value, chapter];
  await putRecord(STORES.chapters, chapter);
}

export async function addChapter(
  courseId: string, number: string, title: string, from: number, to: number,
): Promise<Chapter> {
  const order = chaptersOf(courseId).length + 1;
  const chapter = newChapter(courseId, number, title, from, to, order);
  await saveChapter(chapter);
  await record('chapter', {
    courseId, chapterId: chapter.id,
    label: `Chapter ${number} · ${title}`,
    detail: `${from}–${to}`,
  });
  return chapter;
}

export async function removeChapter(chapterId: string): Promise<void> {
  const doomed = await deleteWhere<Task>(STORES.tasks, task => task.chapterId === chapterId);
  const gone = new Set(doomed);
  tasks.value = tasks.value.filter(task => !gone.has(task.id));
  chapters.value = chapters.value.filter(chapter => chapter.id !== chapterId);
  await deleteRecord(STORES.chapters, chapterId);
}

/**
 * Open a task by number, creating its record if this is the first time.
 *
 * Tasks are created lazily so a chapter of 120 exercises costs nothing until it is worked, and
 * so the count of touched tasks is a real measure of what has been done rather than of what
 * exists.
 */
export async function openTask(chapter: Chapter, number: string): Promise<Task> {
  const existing = taskByNumber(chapter.id, number);
  if (existing) {
    if (existing.status === 'untouched') await setStatus(existing.id, 'doing');
    return existing;
  }
  const task = newTask(chapter, number);
  tasks.value = [...tasks.value, task];
  await putRecord(STORES.tasks, task);
  await record('opened', {
    courseId: chapter.courseId, chapterId: chapter.id, taskId: task.id,
    label: number,
    detail: chapter.title,
  });
  return task;
}

export async function saveTask(task: Task): Promise<void> {
  const updated = { ...task, updatedAt: Date.now(), index: taskIndex(task.number) };
  tasks.value = tasks.value.map(t => (t.id === task.id ? updated : t));
  await putRecord(STORES.tasks, updated);
}

export async function setStatus(taskId: string, status: TaskStatus, mistake?: string): Promise<void> {
  const task = tasks.value.find(t => t.id === taskId);
  if (!task) return;
  const updated = { ...task, status, updatedAt: Date.now(), ...(mistake !== undefined ? { mistake } : {}) };
  tasks.value = tasks.value.map(t => (t.id === taskId ? updated : t));
  await putRecord(STORES.tasks, updated);
  if (status === 'done' || status === 'wrong' || status === 'stuck') {
    const chapter = chapterById(task.chapterId);
    await record(status, {
      courseId: task.courseId, chapterId: task.chapterId, taskId: task.id,
      label: task.number,
      detail: mistake || chapter?.title || '',
      minutes: task.minutes,
    });
  }
}

/** Minutes are added when a task is left, so the log shows time actually spent on the sheet. */
export async function addMinutes(taskId: string, minutes: number): Promise<void> {
  if (minutes <= 0) return;
  const task = tasks.value.find(t => t.id === taskId);
  if (!task) return;
  const updated = { ...task, minutes: task.minutes + minutes, updatedAt: Date.now() };
  tasks.value = tasks.value.map(t => (t.id === taskId ? updated : t));
  await putRecord(STORES.tasks, updated);
}

// ── Projects ───────────────────────────────────────────────────────────────────────────────
// The other half of the landing page. A course is a book; a project is a folder of sheets with
// no numbering and no facit, so nothing below reads or writes a task, a status or a verdict.
//
// What a surface should call, in one place:
//   reads   activeProjects · archivedProjects · recentProjects(n) · projectById · findProjects
//   create  createProject({ name, description, accent })
//   edit    renameProject · describeProject · recolourProject · pinProject
//   file    archiveProject · restoreProject · deleteProject (returns an undo)
//   sheets  addProjectSheet · renameProjectSheet · moveProjectSheet · duplicateProjectSheet ·
//           removeProjectSheet (returns an undo) · touchProject

/** Not archived, pinned first, most recently worked on next. What a browser shows. */
export const activeProjects = computed(() => browse(projects.value, { archived: false }));

export const archivedProjects = computed(() => browse(projects.value, { archived: true }));

export const projectById = (id: string): Project | undefined =>
  projects.value.find(project => project.id === id);

/** The `n` most recently worked-on live projects — the landing page's row, no search involved. */
export const recentProjects = (limit = 3): Project[] => activeProjects.value.slice(0, limit);

export const findProjects = (query: string, archived = false): Project[] =>
  browse(projects.value, { query, archived });

const writeProject = async (project: Project): Promise<Project> => {
  const known = projects.value.some(p => p.id === project.id);
  projects.value = known
    ? projects.value.map(p => (p.id === project.id ? project : p))
    : [...projects.value, project];
  await putRecord(STORES.projects, project);
  return project;
};

/** Read–modify–write against the live record, so two edits in one tick cannot clobber each other. */
const editProject = async (
  id: string, change: (project: Project) => Project,
): Promise<Project | null> => {
  const current = projectById(id);
  if (!current) return null;
  return writeProject(change(current));
};

/**
 * The stored sheet behind a project sheet, created up front.
 *
 * The canvas will happily invent a record for an id it has never seen, but it invents it called
 * "Untitled" — and that name is what the scratch sheet switcher lists. Writing the record here
 * means a project sheet is named everywhere it appears, from the moment it exists. The project
 * record stays the authority on the name; this is the copy the rest of the app can see.
 */
const createSheetRecord = async (projectId: string, sheet: ProjectSheet): Promise<void> => {
  const record = newSheet('kept', sheet.name, sheet.id);
  record.course = `project:${projectId}`;
  await saveSheet(record);
};

export async function createProject(fields: {
  name: string;
  description?: string;
  accent?: ProjectAccent;
}): Promise<Project> {
  const project = newProject(fields);
  await createSheetRecord(project.id, project.sheets[0]!);
  return writeProject(project);
}

export const renameProject = (id: string, name: string): Promise<Project | null> =>
  editProject(id, project => (name.trim() ? touch(project, { name: name.trim() }) : project));

export const describeProject = (id: string, description: string): Promise<Project | null> =>
  editProject(id, project => touch(project, { description: description.trim() }));

export const recolourProject = (id: string, accent: ProjectAccent): Promise<Project | null> =>
  editProject(id, project => touch(project, { accent }));

export const pinProject = (id: string, pinned: boolean): Promise<Project | null> =>
  editProject(id, project => touch(project, { pinned }));

/**
 * Archived, never deleted.
 *
 * Every sheet stays exactly where it was, so restoring is not a recovery — it is a change of
 * mind, and nothing about it can go wrong.
 */
export const archiveProject = (id: string): Promise<Project | null> =>
  editProject(id, project => touch(project, { archived: true, pinned: false }));

export const restoreProject = (id: string): Promise<Project | null> =>
  editProject(id, project => touch(project, { archived: false }));

/**
 * Delete a project for real, and hand back the way to put it back.
 *
 * Archiving is the safe verb and stays the default; this is the one that actually removes the
 * folder. DESIGN.md §8 Do #7 puts it behind an undo rather than a confirm dialog, so every sheet
 * RECORD is read before anything is deleted — an undo that restores the folder and loses the
 * working would be worse than no undo at all, because the row coming back says nothing was lost.
 */
export async function deleteProject(id: string): Promise<(() => Promise<void>) | null> {
  const project = projectById(id);
  if (!project) return null;
  const records = await Promise.all(project.sheets.map(sheet => loadSheet(sheet.id)));
  projects.value = projects.value.filter(p => p.id !== id);
  await deleteRecord(STORES.projects, id);
  for (const sheet of project.sheets) await deleteSheet(sheet.id);
  return async () => {
    for (const [index, sheet] of project.sheets.entries()) {
      const record = records[index];
      if (record) await saveSheet(record);
      else await createSheetRecord(id, sheet);
    }
    await writeProject(project);
  };
}

/** Opening a sheet is working on the project, and that is what "recent" has to mean. */
export const touchProject = (id: string): Promise<Project | null> =>
  editProject(id, project => touch(project, {}));

export async function addProjectSheet(projectId: string, name?: string): Promise<ProjectSheet | null> {
  const project = projectById(projectId);
  if (!project) return null;
  const sheet = newProjectSheet(name?.trim() || nextSheetName(project));
  await createSheetRecord(projectId, sheet);
  await writeProject(insertSheet(project, sheet));
  return sheet;
}

export async function renameProjectSheet(
  projectId: string, sheetId: string, name: string,
): Promise<Project | null> {
  const updated = await editProject(projectId, project => renameSheet(project, sheetId, name));
  const clean = name.trim();
  if (updated && clean) {
    // Best effort: the canvas holds its own copy of the record while the sheet is open and may
    // write the old title back. The project record is the name that is displayed.
    const record = await loadSheet(sheetId);
    if (record) await saveSheet({ ...record, title: clean });
  }
  return updated;
}

export const moveProjectSheet = (
  projectId: string, sheetId: string, delta: number,
): Promise<Project | null> =>
  editProject(projectId, project => moveSheet(project, sheetId, delta));

export async function duplicateProjectSheet(
  projectId: string, sheetId: string,
): Promise<ProjectSheet | null> {
  const project = projectById(projectId);
  const index = project ? sheetAt(project, sheetId) : -1;
  if (!project || index < 0) return null;
  const source = project.sheets[index]!;
  const copy = newProjectSheet(copyName(source.name, project.sheets.map(sheet => sheet.name)));
  const record = await loadSheet(sheetId);
  const made = newSheet('kept', copy.name, copy.id);
  made.course = `project:${projectId}`;
  // The objects are cloned, not shared: a duplicate that aliases its original is a sheet that
  // edits the one it was copied from.
  if (record) made.scene = { objects: record.scene.objects.map(object => ({ ...object })) };
  await saveSheet(made);
  await writeProject(insertSheet(project, copy, index + 1));
  return copy;
}

/**
 * Take a sheet out of a project, and hand back the way to put it back.
 *
 * DESIGN.md §4: destructive actions sit behind an undo, not behind a confirm. The stored record
 * is captured before it is deleted, so the undo restores the working and not just the row.
 */
export async function removeProjectSheet(
  projectId: string, sheetId: string,
): Promise<(() => Promise<void>) | null> {
  const project = projectById(projectId);
  if (!project || project.sheets.length <= 1) return null;
  const { project: without, removed, index } = detachSheet(project, sheetId);
  if (!removed) return null;
  const record = await loadSheet(sheetId);
  await writeProject(without);
  await deleteSheet(sheetId);
  return async () => {
    if (record) await saveSheet(record);
    else await createSheetRecord(projectId, removed);
    await editProject(projectId, current => insertSheet(current, removed, index));
  };
}

// ── Filing a sheet afterwards ──────────────────────────────────────────────────────────────
// PLAN.md §13. Work starts on a scratch sheet, because that is what a person does; the decision
// about what the work IS gets made once there is something worth filing. Two destinations — a
// numbered exercise, or a project — and NEITHER is a copy. The task points at the sheet that was
// already worked on, so its objects, its ids and its undo-able history are the same ones.

export interface Filed {
  ok: boolean;
  /** The record the work now lives in. */
  sheetId: string;
  /** True when the work had to move off the fixed scratch slot. See `claimSheet`. */
  moved: boolean;
  problem: string;
}

const refuse = (problem: string): Filed => ({ ok: false, sheetId: '', moved: false, problem });

/**
 * Take a sheet out of scratch and give it a name, without copying it.
 *
 * A kept sheet keeps its id outright: the record is renamed in place and every task, project and
 * log entry that names it still names the same thing.
 *
 * The scratch PAD cannot, and the reason is worth stating rather than working out twice. `scratch`
 * is a fixed key — one well-known slot so the pad is the same pad across reloads — not an
 * identity. If a filed task pointed at it, wiping the pad tomorrow would wipe the exercise. So the
 * record moves to a key of its own, carrying the same scene: the same objects, with the same ids
 * and the same z-order, not rebuilt and not re-created. The pad it left behind is emptied, which
 * is what a person expects of a pad they have just filed.
 */
async function claimSheet(sheetId: string, title: string, course: string): Promise<{ id: string; moved: boolean } | null> {
  const record = await loadSheet(sheetId);
  if (!record) return null;
  const now = Date.now();

  if (record.id !== SCRATCH_ID) {
    await saveSheet({ ...record, title, course, kind: 'kept', updatedAt: now });
    return { id: record.id, moved: false };
  }

  const moved = newSheet('kept', title);
  await saveSheet({
    ...moved, title, course, createdAt: record.createdAt, updatedAt: now,
    scene: { objects: record.scene.objects },
  });
  await saveSheet({ ...record, scene: emptyScene(), updatedAt: now });
  return { id: moved.id, moved: true };
}

/**
 * This sheet IS exercise 1042 from now on.
 *
 * Refused rather than merged when the number has already been worked: two sheets of working for
 * one exercise is a question only the owner can answer, and silently replacing the older one is
 * the wrong guess. An untouched task record — created by opening the number and never drawn
 * on — is simply repointed, because there is nothing there to lose.
 */
export async function fileSheetAsTask(
  sheetId: string, chapterId: string, number: string,
): Promise<Filed> {
  const chapter = chapterById(chapterId);
  if (!chapter) return refuse('That chapter is not in the workspace.');
  const clean = number.trim();
  if (!clean) return refuse('Give it an exercise number.');

  const existing = taskByNumber(chapterId, clean);
  if (existing && existing.status !== 'untouched' && existing.sheetId !== sheetId) {
    return refuse(`${clean} already has a sheet in this chapter. Pick another number.`);
  }

  const claimed = await claimSheet(sheetId, clean, chapter.courseId);
  if (!claimed) return refuse('That sheet could not be read back.');

  const task: Task = existing
    ? { ...existing, sheetId: claimed.id, status: existing.status === 'untouched' ? 'doing' : existing.status, updatedAt: Date.now() }
    : { ...newTask(chapter, clean), sheetId: claimed.id };
  tasks.value = existing
    ? tasks.value.map(t => (t.id === task.id ? task : t))
    : [...tasks.value, task];
  await putRecord(STORES.tasks, task);
  await record('opened', {
    courseId: chapter.courseId, chapterId: chapter.id, taskId: task.id,
    label: clean,
    detail: `Filed from a scratch sheet · ${chapter.title}`,
  });
  return { ok: true, sheetId: claimed.id, moved: claimed.moved, problem: '' };
}

/** The other destination: named, in a folder, with no number and nothing to be marked against. */
export async function fileSheetToProject(
  sheetId: string, projectId: string, name: string,
): Promise<Filed> {
  const project = projectById(projectId);
  if (!project) return refuse('That project is not in the workspace.');
  const title = name.trim() || nextSheetName(project);

  const claimed = await claimSheet(sheetId, title, `project:${projectId}`);
  if (!claimed) return refuse('That sheet could not be read back.');
  if (project.sheets.some(sheet => sheet.id === claimed.id)) {
    return { ok: true, sheetId: claimed.id, moved: claimed.moved, problem: '' };
  }

  await editProject(projectId, current =>
    insertSheet(current, { id: claimed.id, name: title, createdAt: Date.now() }));
  return { ok: true, sheetId: claimed.id, moved: claimed.moved, problem: '' };
}
